import { useCallback, useEffect, useRef, useState } from 'react';
import { PLAYBACK_CONTROLS_HEIGHT, RULER_HEIGHT, DEFAULT_TRACK_HEIGHT, SCROLLBAR_AREA_HEIGHT } from 'weave-timeline';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import type { NodeId } from 'web';

/** Chrome = everything except the tracks area: playback controls + ruler + scrollbar */
const CHROME_HEIGHT = PLAYBACK_CONTROLS_HEIGHT + RULER_HEIGHT + SCROLLBAR_AREA_HEIGHT;

/** Minimum: show at least 3 tracks + all chrome (playback controls, ruler, scrollbar) */
const MIN_HEIGHT = CHROME_HEIGHT + DEFAULT_TRACK_HEIGHT * 3 + 1;

const MAX_DISPLAYED_LAYERS = 10;
const MAX_HEIGHT = CHROME_HEIGHT + DEFAULT_TRACK_HEIGHT * MAX_DISPLAYED_LAYERS;

interface UseResizeableTimelineOptions {
  nodeId: NodeId;
  layerCount: number;
}

const clampHeight = (height: number) => Math.min(Math.max(height, MIN_HEIGHT), MAX_HEIGHT);

const getInitialHeight = (layerCount: number) => {
  return clampHeight(CHROME_HEIGHT + DEFAULT_TRACK_HEIGHT * layerCount);
};

export function useResizeableTimeline({ nodeId, layerCount }: UseResizeableTimelineOptions) {
  const { getItem, setItem } = useLocalStorage<number>(`timeline-height:${nodeId}`);

  const [timelineH, setTimelineH] = useState(() => {
    const saved = getItem();
    return saved != null ? clampHeight(saved) : getInitialHeight(layerCount);
  });
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => {
      cleanupRef.current?.();
    };
  }, []);

  const handleTimelineResizeMouseDown = useCallback(
    (e: React.MouseEvent) => {
      const startY = e.clientY;
      const startHeight = timelineH;
      let latestHeight = startHeight;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const deltaY = moveEvent.clientY - startY;
        latestHeight = clampHeight(startHeight - deltaY);
        setTimelineH(latestHeight);
      };

      const cleanup = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        cleanupRef.current = null;
      };

      const handleMouseUp = () => {
        cleanup();
        setItem(latestHeight);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      cleanupRef.current = cleanup;
      e.preventDefault();
    },
    [timelineH, setItem],
  );

  return {
    timelineH,
    handleTimelineResizeMouseDown,
  };
}
